/**
 * Course icons come from the backend as either:
 *   - a raw emoji ("🧠", "🏗️")
 *   - a Lucide-style name ("brain", "code", "message-square-text")
 *
 * The existing UI renders `course.icon` inside a `<span>` assuming it's a
 * glyph. To keep rendering dead simple, map named icons to a reasonable
 * emoji equivalent. Anything we don't recognize falls back to a book.
 */
const ICON_MAP: Record<string, string> = {
  brain: '🧠',
  'message-square-text': '💬',
  message: '💬',
  chat: '💬',
  code: '💻',
  scale: '⚖️',
  sparkles: '✨',
  robot: '🤖',
  search: '🔍',
  pen: '✍️',
  edit: '✍️',
  puzzle: '🧩',
  server: '🖥️',
  cpu: '⚙️',
  flame: '🔥',
  fire: '🔥',
  rocket: '🚀',
  eye: '👁️',
  shield: '🛡️',
  telescope: '🔭',
  building: '🏢',
  trophy: '🏆',
  book: '📚',
  'book-open': '📖',
  database: '🗄️',
  globe: '🌐',
  lightbulb: '💡',
  bulb: '💡',
  graduation: '🎓',
  target: '🎯',
  zap: '⚡',
  bolt: '⚡',
  star: '⭐',
  heart: '❤️',
  lock: '🔒',
  key: '🔑',
  link: '🔗',
  wrench: '🔧',
  hammer: '🔨',
  tool: '🛠️',
  map: '🗺️',
  compass: '🧭',
  clock: '⏰',
  calendar: '📅',
  chart: '📊',
  'chart-bar': '📊',
  'chart-line': '📈',
  clipboard: '📋',
  folder: '📁',
  file: '📄',
  microscope: '🔬',
  atom: '⚛️',
  dna: '🧬',
}

/**
 * Return a renderable icon glyph for a course.
 * - If the input already contains at least one non-ASCII char (likely an
 *   emoji), pass it through.
 * - If it's a mapped Lucide-style name, return the mapped emoji.
 * - Otherwise fall back to 📚.
 */
export function courseIconGlyph(icon?: string | null): string {
  if (!icon) return '📚'
  const trimmed = icon.trim()
  if (!trimmed) return '📚'
  // Emojis and other non-ASCII glyphs: keep as-is.
  // eslint-disable-next-line no-control-regex
  if (/[^\x00-\x7F]/.test(trimmed)) return trimmed
  const key = trimmed.toLowerCase()
  return ICON_MAP[key] ?? '📚'
}
